import { useAuthStore } from "../state/auth";

export const kioskScanEndpoint = import.meta.env.VITE_KIOSK_SCAN_API_URL || "/api/scans";

export type ScanStatus = "OK" | "INVALID" | "ERROR";

export interface RecentScan {
  id: string;
  guard_id: string;
  location_id: string;
  timestamp: string;
  received_at: string;
  status: ScanStatus;
  message?: string;
  location_name?: string;
  building?: string;
  floor?: string;
  gps_lat?: number;
  gps_lng?: number;
  gps_source?: 'EVENT' | 'DEVICE' | 'NONE';
  gps_timestamp?: string;
  hospital_name?: string;
  guard_name?: string;
  /** Officer attribution (sign-on system); null when the scan was unattributed. */
  officer_id?: string | null;
  officer_name?: string | null;
  officer_n_number?: string | null;
  /**
   * Device name attributed to the hospital of the scanned checkpoint rather than
   * the device's home hospital (e.g. a Northlake device scanning an EMSK
   * checkpoint shows as "EMSK Device 2"). Resolved server-side; display-only.
   */
  display_device_name?: string | null;
  /** Serial of the physical handset that uploaded the scan; last-resort device label. */
  scanner_serial?: string | null;
  proximity_status?: string | null;
  proximity_distance_m?: number | null;
}

interface RecentScanResponse {
  scans: RecentScan[];
}

/** Explicit ISO date-range override for the recent-scans query. */
export interface ScanDateRange {
  startDate?: string; // ISO 8601
  endDate?: string; // ISO 8601
}

/**
 * Fetches recent scans from the hospital-scoped reports API.
 * This ensures supervisors only see scans from their assigned hospital.
 *
 * Pass `dateRange` to drive the query off an explicit start/end (used by the
 * Live Activity page so the feed follows the Scan Map's time filter). When
 * omitted, the coarse `period` bucket is mapped to a start date instead.
 */
export async function fetchRecentScans(
  hospitalId?: string,
  period: string = 'today',
  dateRange?: ScanDateRange
): Promise<RecentScan[]> {
  const { user } = useAuthStore.getState();
  if (!user?.email) {
    throw new Error("User not authenticated");
  }

  const url = new URL("/api/reports/scans", window.location.origin);
  if (hospitalId) {
    url.searchParams.append("hospital_id", hospitalId);
  }

  if (dateRange?.startDate || dateRange?.endDate) {
    // Explicit range takes precedence over the period buckets.
    if (dateRange.startDate) {
      url.searchParams.append("start_date", dateRange.startDate);
    }
    if (dateRange.endDate) {
      url.searchParams.append("end_date", dateRange.endDate);
    }
  } else {
    // Map period to start_date
    const now = new Date();
    let startDate = new Date();
    startDate.setHours(0, 0, 0, 0); // Default Today

    if (period === 'week') {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (period === 'month') {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (period === 'quarter') {
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    }

    url.searchParams.append("start_date", startDate.toISOString());
  }

  try {
    const headers: Record<string, string> = {
      "Accept": "application/json",
      "x-user-email": user.email,
    };
    const { token } = useAuthStore.getState();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url.toString(), {
      headers,
      cache: "no-store"
    });

    if (!response.ok) {
      throw new Error(`Failed to load scans (${response.status})`);
    }

    const data = await response.json();

    const scans: RecentScan[] = (data.scans || []).map((scan: any) => {
      let locationId = scan.location_id || "unknown";
      let parsedName: string | null = null;
      let parsedSuite: string | null = null;
      let parsedFloor: string | null = null;

      // If location_id is missing but raw_payload is present, try to parse it
      if ((!scan.location_id || scan.location_id === "unknown") && scan.raw_payload) {
        try {
          if (scan.raw_payload.startsWith('{')) {
            const parsed = JSON.parse(scan.raw_payload);
            if (parsed.location_id) locationId = parsed.location_id;
            // Extract human-readable fields from JSON QR payload
            if (parsed.name) parsedName = parsed.name;
            if (parsed.suite) parsedSuite = parsed.suite;
            if (parsed.floor) parsedFloor = String(parsed.floor);
          } else if (scan.raw_payload.startsWith("LOC|")) {
            locationId = scan.raw_payload.replace("LOC|", "");
          }
        } catch (e) {
          // Ignore parse errors, stick with existing locationId
        }
      }

      // Final fallback for QR payload as well
      if (locationId === "unknown" && scan.qr_payload && scan.qr_payload.startsWith("LOC|")) {
        locationId = scan.qr_payload.replace("LOC|", "");
      }

      // Build human-readable location name: prefer DB name, then parsed JSON name, then ID
      let locationName = scan.location_name;
      if (!locationName && parsedName) {
        locationName = parsedName;
      }
      if (!locationName) {
        locationName = locationId !== "unknown" ? locationId : "Unmapped Location";
      }

      // Build building label from parsed suite or DB building
      const building = scan.building || parsedSuite || "Unknown Building";
      const floor = scan.floor || parsedFloor || "—";

      // Map proximity_status to scan status for the filter
      let scanStatus: ScanStatus = "OK";
      if (scan.proximity_status === "OUT_OF_RANGE") {
        scanStatus = "INVALID";
      } else if (scan.proximity_status === "GPS_UNAVAILABLE") {
        scanStatus = "ERROR";
      }

      // Build message for invalid scans
      let message: string | undefined;
      if (scan.proximity_status === "OUT_OF_RANGE" && scan.proximity_distance_m) {
        message = `Scan was ${Math.round(scan.proximity_distance_m)}m from checkpoint (max 100m)`;
      } else if (scan.proximity_status === "GPS_UNAVAILABLE") {
        message = "GPS was unavailable during this scan";
      }

      return {
        id: scan.id || Math.random().toString(36).substring(7),
        guard_id: scan.guard_name || scan.guard_id || "N/A",
        location_id: locationId,
        timestamp: scan.scanned_at || new Date().toISOString(),
        received_at: scan.received_at || scan.scanned_at || new Date().toISOString(),
        status: scanStatus,
        message: message,
        location_name: locationName,
        building: building,
        floor: floor,
        gps_lat: scan.gps_lat,
        gps_lng: scan.gps_lng,
        gps_source: scan.gps_source || "NONE",
        gps_timestamp: scan.gps_timestamp,
        hospital_name: scan.hospital_name || "Unknown Hospital",
        guard_name: scan.guard_name || undefined,
        officer_id: scan.officer_id || null,
        officer_name: scan.officer_name || null,
        officer_n_number: scan.officer_n_number || null,
        display_device_name: scan.display_device_name || undefined,
        scanner_serial: scan.scanner_serial || null,
        proximity_status: scan.proximity_status || null,
        proximity_distance_m: scan.proximity_distance_m ? parseFloat(scan.proximity_distance_m) : null,
      };
    });

    return scans;
  } catch (error) {
    console.error("fetchRecentScans failed:", error);
    return [];
  }
}

/** One Eastern-Time day of aggregated scan activity (server-side counts). */
export interface ScanDaySummary {
  day: string; // YYYY-MM-DD (Eastern Time)
  count: number;
  alerts: number;
  hospitals: { name: string; count: number }[]; // most active first; "" = no hospital
  hourly: number[]; // 24 buckets, Eastern Time
}

/**
 * Per-day scan totals for a date window, counted in the database. Used by the
 * Calendar's Month and Week views so their totals are never capped by the raw
 * scan list's 50,000-row limit. Unlike fetchRecentScans this throws on failure,
 * so the page can show an error instead of an empty (misleading) calendar.
 */
export async function fetchScanDaySummary(opts: {
  startDay: string; // YYYY-MM-DD, inclusive
  endDay: string; // YYYY-MM-DD, inclusive
  hospitalId?: string;
  officerId?: string; // uuid | "__unattributed"
}): Promise<ScanDaySummary[]> {
  const { user, token } = useAuthStore.getState();
  if (!user?.email) throw new Error("User not authenticated");

  const url = new URL("/api/reports/scans", window.location.origin);
  url.searchParams.set("summary", "day");
  url.searchParams.set("start_day", opts.startDay);
  url.searchParams.set("end_day", opts.endDay);
  if (opts.hospitalId) url.searchParams.set("hospital_id", opts.hospitalId);
  if (opts.officerId) url.searchParams.set("officer_id", opts.officerId);

  const headers: Record<string, string> = { Accept: "application/json", "x-user-email": user.email };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(url.toString(), { headers, cache: "no-store" });
  if (!response.ok) throw new Error(`Failed to load activity summary (${response.status})`);
  const data = await response.json();
  return Array.isArray(data?.days) ? data.days : [];
}
